import type { DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import type { ExpertReportFeedbackItem } from '@/entities/expert-report';
import { useLazyExpertReportFeedback } from '@/entities/expert-report';
import {
  fromDataGridFilters,
  fromDataGridSorting,
  toDataGridFilters,
  toDataGridSorting,
  useDataGridColumnsSettings,
  useTableState,
} from '@/entities/settings';
import { PageContainer } from '@/shared/ui';
import {
  DEFAULT_FEEDBACK_COLUMN_ORDER,
  DEFAULT_FEEDBACK_COLUMN_VISIBILITY,
  DEFAULT_FEEDBACK_SORTING,
  FEEDBACK_PAGE_SIZE,
  FEEDBACK_TABLE_NAME,
} from '../model/constants';
import { toFeedbackFilters } from '../model/filters';
import { ExpertReportFeedbackTable } from './ExpertReportFeedbackTable';

/**
 * Контейнер раздела «Обратная связь»: держит отбор, сортировку и настройки колонок (они
 * сохраняются за пользователем), тянет очередное окно правок по мере прокрутки и отдаёт
 * его презентеру.
 */
export const ExpertReportFeedbackPage: FC = () => {
  const { settings, actions } = useTableState(FEEDBACK_TABLE_NAME, 'localStorage', {
    sorting: DEFAULT_FEEDBACK_SORTING,
  });
  const [totalCount, setTotalCount] = useState(0);

  const filters = useMemo(() => toDataGridFilters(settings.filters), [settings.filters]);
  const sorting = useMemo(() => toDataGridSorting(settings.sorting), [settings.sorting]);
  const { gridColumnProps } = useDataGridColumnsSettings({
    settings,
    actions,
    filters,
    sorting,
    defaultVisibility: DEFAULT_FEEDBACK_COLUMN_VISIBILITY,
    defaultOrder: DEFAULT_FEEDBACK_COLUMN_ORDER,
  });

  const { refetch } = useLazyExpertReportFeedback();

  // Подпись состава запроса: смена отбора или порядка сбрасывает загруженные порции,
  // и список запрашивается заново с начала
  const cacheVersion = useMemo(
    () => `${JSON.stringify(settings.filters)}|${JSON.stringify(settings.sorting)}`,
    [settings.filters, settings.sorting],
  );

  // Вместе с составом запроса сбрасывается и общее количество: окно грид считает по нему,
  // и устаревшее значение обрезало бы первую порцию нового отбора
  const lastCacheVersionRef = useRef(cacheVersion);
  if (lastCacheVersionRef.current !== cacheVersion) {
    lastCacheVersionRef.current = cacheVersion;
    setTotalCount(0);
  }

  const fetchPage = useCallback(
    async (offset: number, limit: number): Promise<ExpertReportFeedbackItem[]> => {
      const data = await refetch({ ...toFeedbackFilters(filters, sorting), offset, limit });
      setTotalCount(data.total);
      return data.items;
    },
    [refetch, filters, sorting],
  );

  const handleFiltersChange = useCallback(
    (next: DataGridFilterState) => actions.onFiltersChange(fromDataGridFilters(next)),
    [actions],
  );

  const handleSortingChange = useCallback(
    (next: DataGridSortingState) => actions.onSortingChange(fromDataGridSorting(next)),
    [actions],
  );

  return (
    // Список занимает всю ширину рабочей области — как список диалогов
    <PageContainer enableScroll={false} limitedWidth={false} className="flex flex-col gap-4 h-full w-full">
      <ExpertReportFeedbackTable
        totalCount={totalCount}
        pageSize={FEEDBACK_PAGE_SIZE}
        fetchPage={fetchPage}
        cacheVersion={cacheVersion}
        sorting={sorting}
        filters={filters}
        columnState={gridColumnProps}
        onSortingChange={handleSortingChange}
        onFiltersChange={handleFiltersChange}
      />
    </PageContainer>
  );
};
